<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── TOPBAR ── -->
      <div class="topbar">
        <div class="topbar-left">
          <div class="topbar-avatar" @click="$router.push('/member')">
            <img v-if="user.image" :src="user.image" alt="Profile" />
            <span v-else>{{ initials }}</span>
          </div>
          <div class="topbar-meta">
            <p class="topbar-greeting">Logged in as</p>
            <p class="topbar-name">{{ user.fullName || 'Member' }}</p>
          </div>
        </div>
        <div class="topbar-right">
          <button class="icon-btn" @click="$router.push('/member')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </button>
          <button class="icon-btn" @click="$router.push('/notifications')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
          </button>
          <button class="icon-btn" @click="logout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </div>

      <!-- ── MISSION STRIP ── -->
      <div class="mission-strip">
        <p class="mission-eyebrow">The Inner Network</p>
        <h1 class="mission-title">Travel Intelligence, <em>First</em></h1>
        <p class="mission-sub">
          Wherever a reciprocal member lands, they should already know where to eat, sleep, and be looked after.
          Everything below is built for that — sourced from members who have already been there, and turned into
          leverage for the arrangements we bring back to them.
        </p>
      </div>

      <!-- ── TRAVEL INTELLIGENCE ── -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Concierge</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">Travel <em>Intelligence</em></h2>
      <p class="section-lede">Built from what members have already told us — where they're headed, and where they've asked us to arrange it officially.</p>

      <!-- Active city card (dynamic) -->
      <div v-if="citySpotlight" class="city-card">
        <div class="city-card-header">
          <div>
            <p class="city-card-city">{{ citySpotlight.city }}</p>
            <p class="city-card-tagline">{{ citySpotlight.tagline }}</p>
          </div>
          <div class="city-live">
            <div class="city-live-dot"></div>
            <span>Live</span>
          </div>
        </div>
        <div class="city-divider"></div>
        <div class="city-stats">
          <div class="city-stat">
            <span class="city-stat-value">{{ citySpotlight.travelerCount }}</span>
            <span class="city-stat-label">Members Traveling</span>
          </div>
          <div class="city-stat">
            <span class="city-stat-value">{{ citySpotlight.recommendationCount }}</span>
            <span class="city-stat-label">Recommendations</span>
          </div>
        </div>
        <div class="city-cta" @click="goToCityInExplore(citySpotlight.city)">
          <div class="city-cta-tick"></div>
          <span>View {{ citySpotlight.city }} in Explore</span>
          <div class="city-cta-tick"></div>
          <div style="flex:1"></div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="width:14px;height:14px;color:rgba(210,175,100,0.4)"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </div>
      </div>

      <!-- No trips shared yet -->
      <div v-else class="city-card city-card-empty">
        <p class="city-empty-title">No trips shared yet</p>
        <p class="city-empty-sub">The first member to post where they're headed will show up here.</p>
        <div class="city-cta" @click="$router.push('/explore')">
          <div class="city-cta-tick"></div>
          <span>Share Where You're Headed</span>
          <div class="city-cta-tick"></div>
        </div>
      </div>

      <!-- Members in city strip -->
      <div v-if="citySpotlight && citySpotlight.travelers.length" class="members-in-city">
        <div class="mic-header">
          <span class="mic-label">Members Heading to {{ citySpotlight.city }}</span>
          <span class="mic-view-all" @click="goToCityInExplore(citySpotlight.city)">View all</span>
        </div>
        <div class="mic-avatars">
          <div
            v-for="(m, i) in citySpotlight.travelers.slice(0, 5)"
            :key="m.uid"
            class="mic-avatar"
            :style="{ marginLeft: i === 0 ? '0' : '-10px', zIndex: 5 - i }"
          >{{ m.initials }}</div>
          <span class="mic-count">{{ citySpotlight.travelers.length }} members</span>
        </div>
      </div>

      <!-- ── WHAT MEMBERS ARE ASKING FOR ── -->
      <div class="section-hd" style="margin-top: 56px;">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">From Member Recommendations</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">Requested <em>Arrangements</em></h2>
      <p class="section-lede">
        Members are already telling us what they'd want made official. This is the leverage we bring to a business —
        proof that our members are already walking through the door.
      </p>

      <div v-if="topRequestedPlaces.length" class="wanted-list">
        <div v-for="(place, i) in topRequestedPlaces" :key="place.key" class="wanted-item">
          <div class="wanted-rank">{{ i + 1 }}</div>
          <div class="wanted-body">
            <p class="wanted-name">{{ place.businessName }}</p>
            <p class="wanted-meta">{{ place.city }} · {{ place.category }}</p>
            <p v-if="place.quote" class="wanted-quote">“{{ place.quote }}”</p>
          </div>
          <div class="wanted-count">
            <span class="wanted-count-value">{{ place.count }}</span>
            <span class="wanted-count-label">members</span>
          </div>
        </div>
      </div>
      <div v-else class="empty-state compact">
        <p class="empty-sub">No arrangement requests yet — they'll show up here as members post in Explore.</p>
      </div>

      <div class="wanted-cta" @click="$router.push('/explore')">
        <div class="wanted-cta-tick"></div>
        <span>See All Recommendations in Explore</span>
        <div class="wanted-cta-tick"></div>
      </div>

      <!-- ── FELLOW TRAVELERS (secondary, travel-framed) ── -->
      <div class="section-hd minor" style="margin-top: 56px;">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Also Nearby</span>
        <div class="section-rule"></div>
      </div>
      <h3 class="section-title-minor">Fellow <em>Travelers</em></h3>
      <p class="section-lede minor">Members whose cities and clubs overlap with yours. Travel intelligence is the point — an introduction is just a nice extra.</p>

      <div v-if="isLoadingConnections" class="loading-row compact">
        <p class="loading-text">Curating…</p>
      </div>

      <div v-else class="scroll-row">
        <div
          v-for="connection in potentialConnections.slice(0, 8)"
          :key="connection.uid"
          class="fellow-card"
          @click="viewMemberDetail(connection.uid)"
        >
          <img v-if="connection.image" :src="connection.image" :alt="connection.fullName" class="fellow-photo" />
          <div v-else class="fellow-photo-placeholder">{{ (connection.fullName || '?').charAt(0) }}</div>
          <p class="fellow-name">{{ connection.fullName }}</p>
          <p class="fellow-club">{{ connection.club || connection.role || 'Member' }}</p>
          <button class="fellow-btn" @click.stop="connect(connection)">Say Hello</button>
        </div>
        <div v-if="!potentialConnections.length" class="empty-state compact">
          <p class="empty-sub">No overlapping members to surface right now.</p>
        </div>
      </div>

      <!-- ── MY CONNECTIONS ── -->
      <div class="section-hd minor" style="margin-top: 44px;">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Your Circle</span>
        <div class="section-rule"></div>
      </div>
      <h3 class="section-title-minor" style="margin-bottom: 20px">My <em>Connections</em></h3>

      <div v-if="isLoadingConnections" class="loading-row">
        <p class="loading-text">Loading your circle…</p>
      </div>
      <div v-else-if="myConnections.length" class="connections-row">
        <div
          v-for="connection in myConnections"
          :key="connection.uid"
          class="conn-item"
          @click="viewMemberDetail(connection.uid)"
        >
          <div class="conn-avatar">
            <img v-if="connection.image" :src="connection.image" :alt="connection.fullName" />
            <span v-else>{{ (connection.fullName || '?').charAt(0) }}</span>
          </div>
          <span class="conn-name">{{ (connection.fullName || '').split(' ')[0] }}</span>
        </div>
      </div>
      <div v-else class="empty-connections">
        <p class="empty-sub">Your connections will appear here.</p>
      </div>

    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item active" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
        <div class="nav-dot"></div>
      </button>
      <button class="nav-item" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
      </button>
      <button class="nav-item" @click="$goToProfile()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
      </button>
    </nav>

  </div>
</template>

<script>
import { getAuth, signOut } from "firebase/auth";
import { db } from "@/firebase";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";

// Travel plan posts should disappear once the trip is over. If the poster
// gave a real return date, that's authoritative. If they didn't (the date
// field is optional, and older posts predate it existing at all), there's
// no way to know when their trip actually ends — so instead of showing it
// forever, it's hidden automatically this many days after it was posted.
const TRAVEL_PLAN_AUTO_EXPIRE_DAYS = 7;

function hasTravelDatePassed(post) {
  const now = new Date();

  if (post.travelEndDate) {
    const end = post.travelEndDate?.toDate
      ? post.travelEndDate.toDate()
      : new Date(post.travelEndDate);
    return now > end;
  }

  const posted = post.timestamp?.toDate
    ? post.timestamp.toDate()
    : (post.timestamp ? new Date(post.timestamp) : null);
  if (!posted) return false;

  const ageMs = now - posted;
  return ageMs > TRAVEL_PLAN_AUTO_EXPIRE_DAYS * 24 * 60 * 60 * 1000;
}

function isTravelPlanExpired(post) {
  // Used for filtering/hiding only. A saved post's date can still have
  // passed grammatically (see hasTravelDatePassed) — it just isn't
  // treated as "expired" for visibility purposes, since the member
  // chose to keep it around.
  return hasTravelDatePassed(post) && post.saved !== true;
}

export default {
  name: "Home",

  data() {
    return {
      user: {},
      potentialConnections: [],
      myConnections: [],
      isLoadingConnections: true,
      explorePosts: [],
      nowTick: Date.now(), // ticks periodically so expired travel plans disappear live, not just on next reload
    };
  },

  computed: {
    initials() {
      const parts = (this.user.fullName || "").trim().split(/\s+/);
      return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "M";
    },

    recommendationPosts() {
      return this.explorePosts.filter(p => (p.postType || "recommendation") !== "travel_plan");
    },

    travelPlanPosts() {
      this.nowTick; // eslint-disable-line no-unused-expressions
      return this.explorePosts.filter(p => p.postType === "travel_plan" && !isTravelPlanExpired(p));
    },

    // The leverage data: which businesses have the most members asking for an
    // official arrangement, network-wide. Quotes are pulled from a real post,
    // never fabricated.
    topRequestedPlaces() {
      const byPlace = new Map();
      this.recommendationPosts.forEach(p => {
        if (!p.businessName) return;
        const arrangementCount = Array.isArray(p.arrangementInterest) ? p.arrangementInterest.length : 0;
        const key = `${p.city || ""}::${p.businessName}`.toLowerCase();
        const existing = byPlace.get(key);
        const count = existing ? existing.count + arrangementCount : arrangementCount;
        byPlace.set(key, {
          key,
          businessName: p.businessName,
          city: p.city || "Unknown City",
          category: p.category || "Recommendation",
          quote: existing?.quote || (p.text ? p.text.trim() : ""),
          count,
        });
      });
      return [...byPlace.values()]
        .filter(p => p.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 4);
    },

    // Which city has the most members currently declaring travel plans there —
    // that becomes the home-page spotlight. Ties broken by recommendation volume.
    citySpotlight() {
      if (!this.travelPlanPosts.length) return null;

      const byCity = new Map();
      this.travelPlanPosts.forEach(p => {
        if (!p.city) return;
        const entry = byCity.get(p.city) || { city: p.city, travelers: new Map(), allDatesPassed: true };
        if (p.uid && !entry.travelers.has(p.uid)) {
          entry.travelers.set(p.uid, {
            uid: p.uid,
            initials: (p.fullName || "?").trim().split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?",
          });
        }
        if (!hasTravelDatePassed(p)) entry.allDatesPassed = false;
        byCity.set(p.city, entry);
      });

      const recCountByCity = new Map();
      this.recommendationPosts.forEach(p => {
        if (!p.city) return;
        recCountByCity.set(p.city, (recCountByCity.get(p.city) || 0) + 1);
      });

      const ranked = [...byCity.values()].sort((a, b) => b.travelers.size - a.travelers.size);
      const top = ranked[0];
      if (!top) return null;

      const travelerCount = top.travelers.size;
      const tagline = top.allDatesPassed
        ? `${travelerCount} ${travelerCount === 1 ? "member was" : "members were"} at ${top.city}.`
        : `${travelerCount} ${travelerCount === 1 ? "member is" : "members are"} heading to ${top.city} right now.`;
      return {
        city: top.city,
        travelerCount,
        recommendationCount: recCountByCity.get(top.city) || 0,
        travelers: [...top.travelers.values()],
        tagline,
      };
    },
  },

  async mounted() {
    await this.fetchUserData();
    await Promise.all([this.fetchConnections(), this.fetchExplorePosts()]);
    this._expireTickInterval = setInterval(() => { this.nowTick = Date.now(); }, 15000);
  },

  beforeUnmount() {
    clearInterval(this._expireTickInterval);
  },

  methods: {
    async fetchExplorePosts() {
      try {
        const q = query(collection(db, "explore"), where("isPublic", "==", true));
        const snap = await getDocs(q);
        this.explorePosts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (error) {
        console.error("Error fetching explore posts for home:", error);
      }
    },

    goToCityInExplore(city) {
      this.$router.push({ path: "/explore", query: { city } });
    },

    async fetchUserData() {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        const userDoc = doc(db, "user", currentUser.uid);
        const userSnap = await getDoc(userDoc);
        if (userSnap.exists()) {
          this.user = userSnap.data();
        }
      }
    },

    async fetchConnections() {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const myConnectionsRef = collection(db, `user/${currentUser.uid}/connections`);
      const myConnectionsSnap = await getDocs(myConnectionsRef);
      const rawConnections = myConnectionsSnap.docs.map(d => ({ ...d.data(), uid: d.id }));

      // Resolve each connection against their live user doc rather than trusting
      // whatever was denormalized onto the connection doc at write time — that
      // data can be missing entirely (older/incomplete connections) or go stale
      // if the person's name or photo changes later.
      //
      // If the user doc is gone entirely (account deleted), drop the
      // connection from the list rather than falling back to the raw,
      // nameless/imageless doc — that fallback is how a deleted account
      // was showing up here as a blank "?" avatar with no name. This
      // only fixes what's DISPLAYED; the stale `connections/{deletedUid}`
      // doc itself still exists in Firestore (account deletion only ever
      // cleans up the deleted user's own connections subcollection, not
      // the reverse references sitting in everyone ELSE's), so it'll
      // keep getting fetched and filtered out here every load until that
      // gets cleaned up server-side.
      const resolvedConnections = await Promise.all(
        rawConnections.map(async (conn) => {
          try {
            const userSnap = await getDoc(doc(db, "user", conn.uid));
            if (userSnap.exists()) {
              const userData = userSnap.data();
              return { ...conn, fullName: userData.fullName || conn.fullName || "", image: userData.image || conn.image || "" };
            }
            return null;
          } catch (e) {
            console.error("Failed to resolve connection profile:", e);
            return conn; // a fetch error isn't the same as "this account is gone" — keep it rather than guess
          }
        })
      );
      this.myConnections = resolvedConnections.filter(Boolean);

      const allUsersRef = collection(db, "user");
      const allUsersSnap = await getDocs(allUsersRef);
      this.potentialConnections = allUsersSnap.docs
        .map(d => ({ ...d.data(), uid: d.id }))
        .filter(user => {
          if (this.myConnections.some(conn => conn.uid === user.uid)) return false;
          if (user.uid === currentUser.uid) return false;
          const effectiveType = (user.type || user.role || "").toLowerCase();
          if (!effectiveType) return false;
          return effectiveType !== "admin" && effectiveType !== "applicant";
        });

      this.isLoadingConnections = false;
    },

    connect(connection) {
      console.log("Connecting to", connection.fullName);
    },

    logout() {
      const auth = getAuth();
      signOut(auth)
        .then(() => { this.$router.push("/"); })
        .catch(error => { console.error("Error during logout:", error); });
    },

    // viewMemberProfile (routing to the "MemberProfile" route /
    // MemberProfileView.vue) removed — every member card on this page now
    // goes to viewMemberDetail's richer MemberDetailView.vue instead, which
    // has the Club Affiliation / Professional / Travel / Bio sections and
    // the actual Request Introduction flow that MemberProfileView.vue never
    // had. If nothing else in the app still routes to "MemberProfile", that
    // route and MemberProfileView.vue can likely be deleted outright — I
    // don't have the router config to confirm that from here.
    viewMemberDetail(uid) {
      this.$router.push({ name: "MemberDetail", params: { id: uid } });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

/* ── ROOT ── */
.tin-root {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #070708;
  font-family: 'Montserrat', sans-serif;
}

/* ── LAYERS ── */
.ambient {
  position: fixed; top: -300px; left: 50%; transform: translateX(-50%);
  width: 1000px; height: 800px; pointer-events: none; z-index: 0;
  background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%);
}
.ambient-floor {
  position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%);
  width: 700px; height: 400px; pointer-events: none; z-index: 0;
  background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%);
}
.grain {
  position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E");
  background-size: 180px 180px;
}
.frame {
  position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none;
}
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

/* ── PAGE ── */
.page {
  position: relative; z-index: 10;
  max-width: 1100px; margin: 0 auto;
  padding: 36px 48px 140px;
  animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both;
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── TOPBAR ── */
.topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(210,175,100,0.1);
  margin-bottom: 52px;
}
.topbar-left { display: flex; align-items: center; gap: 20px; }
.topbar-avatar {
  width: 60px; height: 60px; border-radius: 50%;
  border: 1px solid rgba(210,175,100,0.3);
  overflow: hidden; cursor: pointer;
  background: rgba(210,175,100,0.06);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cormorant', serif; font-size: 22px; font-weight: 300;
  color: rgba(210,175,100,0.7);
  transition: border-color 0.2s;
  flex-shrink: 0;
}
.topbar-avatar:hover { border-color: rgba(210,175,100,0.6); }
.topbar-avatar img { width: 100%; height: 100%; object-fit: cover; }
.topbar-greeting {
  font-size: 10px; font-weight: 500; letter-spacing: 3px;
  color: rgba(210,175,100,0.4); text-transform: uppercase; margin-bottom: 5px;
}
.topbar-name {
  font-family: 'Cormorant', serif; font-size: 28px; font-weight: 400;
  color: #edeae2; letter-spacing: -0.3px; line-height: 1;
}
.topbar-right { display: flex; align-items: center; gap: 6px; }
.icon-btn {
  width: 42px; height: 42px;
  border: 1px solid rgba(237,232,223,0.08);
  background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: rgba(237,232,223,0.3);
  transition: border-color 0.2s, color 0.2s;
}
.icon-btn:hover { border-color: rgba(210,175,100,0.3); color: rgba(210,175,100,0.7); }
.icon-btn svg { width: 17px; height: 17px; }

/* ── SECTION HEADER ── */
.section-hd {
  display: flex; align-items: center; gap: 20px; margin-bottom: 20px;
}
.section-rule {
  flex: 1; height: 1px;
  background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent);
}
.section-rule.l {
  background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18));
}
.section-eyebrow {
  font-size: 9px; font-weight: 600; letter-spacing: 4px;
  color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap;
}
.section-title {
  font-family: 'Cormorant', serif; font-size: 40px; font-weight: 300;
  color: #edeae2; letter-spacing: -0.5px; margin-bottom: 32px; line-height: 1;
}
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.section-lede { font-size: 13px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.4); max-width: 620px; margin-top: -18px; margin-bottom: 28px; }

/* ── MISSION STRIP ── */
.mission-strip {
  padding-bottom: 40px; margin-bottom: 48px;
  border-bottom: 1px solid rgba(210,175,100,0.1);
}
.mission-eyebrow {
  font-size: 9px; font-weight: 600; letter-spacing: 4px;
  color: rgba(210,175,100,0.5); text-transform: uppercase; margin-bottom: 14px;
}
.mission-title {
  font-family: 'Cormorant', serif; font-size: 52px; font-weight: 300;
  color: #edeae2; letter-spacing: -0.5px; line-height: 1.05; margin-bottom: 18px;
}
.mission-title em { font-style: italic; color: rgba(210,175,100,0.85); font-weight: 300; }
.mission-sub {
  font-size: 14px; font-weight: 300; line-height: 1.8;
  color: rgba(237,232,223,0.45); max-width: 620px;
}

/* ── MINOR (DEMOTED) SECTIONS ── */
.section-hd.minor { margin-bottom: 14px; }
.section-hd.minor .section-eyebrow { color: rgba(210,175,100,0.3); letter-spacing: 3px; }
.section-title-minor {
  font-family: 'Cormorant', serif; font-size: 26px; font-weight: 300;
  color: rgba(237,232,223,0.75); letter-spacing: -0.2px; margin-bottom: 10px; line-height: 1.1;
}
.section-title-minor em { font-style: italic; color: rgba(210,175,100,0.6); font-weight: 300; }
.section-lede.minor { font-size: 12px; margin-top: 0; margin-bottom: 20px; color: rgba(237,232,223,0.3); }
.loading-row.compact { padding: 32px 0; flex-direction: row; gap: 10px; }
.empty-state.compact { padding: 32px 24px; }

/* ── FELLOW TRAVELERS (compact strip) ── */
.fellow-card {
  flex: 0 0 150px; display: flex; flex-direction: column; align-items: center; text-align: center;
  border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02);
  padding: 20px 14px; cursor: pointer; transition: border-color 0.2s;
}
.fellow-card:hover { border-color: rgba(210,175,100,0.28); }
.fellow-photo, .fellow-photo-placeholder {
  width: 56px; height: 56px; border-radius: 50%; object-fit: cover;
  border: 1px solid rgba(210,175,100,0.2); margin-bottom: 12px;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cormorant', serif; font-size: 20px; font-weight: 300;
  color: rgba(210,175,100,0.5); background: rgba(210,175,100,0.05);
}
.fellow-name { font-family: 'Cormorant', serif; font-size: 16px; font-weight: 400; color: #edeae2; line-height: 1.2; margin-bottom: 3px; }
.fellow-club { font-size: 8px; font-weight: 500; letter-spacing: 1px; text-transform: uppercase; color: rgba(237,232,223,0.25); margin-bottom: 14px; }
.fellow-btn {
  font-size: 8px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase;
  color: rgba(210,175,100,0.6); background: transparent; border: 1px solid rgba(210,175,100,0.25);
  padding: 7px 12px; cursor: pointer; transition: border-color 0.2s, color 0.2s;
}
.fellow-btn:hover { border-color: rgba(210,175,100,0.6); color: rgba(210,175,100,0.95); }

/* ── LOADING ── */
.loading-row {
  display: flex; flex-direction: column; align-items: center;
  gap: 20px; padding: 48px 0; margin-bottom: 48px;
}
.loading-seal {
  position: relative; width: 48px; height: 48px;
  display: flex; align-items: center; justify-content: center;
}
.seal-ring {
  position: absolute; inset: 0; border-radius: 50%;
  border: 1px solid rgba(210,175,100,0.2);
  animation: spinSlow 6s linear infinite;
}
.seal-ring-dashed {
  position: absolute; inset: 5px; border-radius: 50%;
  border: 1px dashed rgba(210,175,100,0.15);
  animation: spinSlow 4s linear infinite reverse;
}
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond {
  width: 12px; height: 12px;
  border: 1px solid rgba(210,175,100,0.6);
  transform: rotate(45deg); position: relative; z-index: 1;
}
.seal-diamond::after {
  content: ''; position: absolute; inset: 2px;
  background: rgba(210,175,100,0.3);
}
.loading-text {
  font-size: 10px; font-weight: 500; letter-spacing: 3px;
  color: rgba(237,232,223,0.2); text-transform: uppercase;
}

/* ── MEMBER CARDS ── */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 64px;
}
.member-card {
  border: 1px solid rgba(210,175,100,0.1);
  background: rgba(210,175,100,0.02);
  cursor: pointer;
  transition: border-color 0.3s;
  position: relative; overflow: hidden;
}
.member-card::before, .member-card::after {
  content: ''; position: absolute; width: 14px; height: 14px; z-index: 1;
}
.member-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.member-card::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }
.member-card:hover { border-color: rgba(210,175,100,0.3); }
.member-card:hover .card-photo { filter: brightness(1.06); }
.card-photo {
  width: 100%; height: 260px;
  object-fit: cover; object-position: top center; display: block;
  filter: brightness(0.93); transition: filter 0.4s;
}
.card-photo-placeholder {
  width: 100%; height: 260px;
  background: rgba(210,175,100,0.04);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cormorant', serif; font-size: 64px; font-weight: 300;
  color: rgba(210,175,100,0.2);
}
.card-body { padding: 22px 24px 0; }
.card-name {
  font-family: 'Cormorant', serif; font-size: 24px; font-weight: 400;
  color: #edeae2; letter-spacing: -0.2px; margin-bottom: 6px; line-height: 1.1;
}
.card-club {
  font-size: 10px; font-weight: 500; letter-spacing: 2.5px;
  color: rgba(210,175,100,0.45); text-transform: uppercase; margin-bottom: 22px;
}
.card-btn {
  width: 100%;
  border: none; border-top: 1px solid rgba(210,175,100,0.1);
  background: transparent; padding: 17px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 12px;
  position: relative; overflow: hidden;
  transition: background 0.3s;
}
.card-btn::before {
  content: ''; position: absolute; inset: 0;
  background: rgba(210,175,100,0.05);
  transform: scaleX(0); transform-origin: left;
  transition: transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94);
}
.member-card:hover .card-btn::before { transform: scaleX(1); }
.member-card:hover .card-btn { border-top-color: rgba(210,175,100,0.2); }
.card-btn-tick { width: 18px; height: 1px; background: rgba(210,175,100,0.3); transition: width 0.3s; flex-shrink: 0; }
.member-card:hover .card-btn-tick { width: 24px; }
.card-btn-label { font-size: 10px; font-weight: 600; letter-spacing: 3.5px; color: rgba(210,175,100,0.7); text-transform: uppercase; }
.empty-state {
  grid-column: 1 / -1; padding: 64px 0; text-align: center;
  border: 1px solid rgba(210,175,100,0.08);
}
.empty-title {
  font-family: 'Cormorant', serif; font-size: 24px; font-weight: 300;
  color: rgba(237,232,223,0.3); margin-bottom: 10px;
}
.empty-sub {
  font-size: 11px; font-weight: 400; letter-spacing: 1px;
  color: rgba(237,232,223,0.15); text-transform: uppercase;
}

/* ── SHARED HORIZONTAL SCROLL ROW ── */
.scroll-row {
  display: flex; gap: 16px;
  overflow-x: auto; padding-bottom: 4px;
  margin-bottom: 56px;
  scrollbar-width: thin;
  scrollbar-color: rgba(210,175,100,0.15) transparent;
}
.scroll-row::-webkit-scrollbar { height: 1px; }
.scroll-row::-webkit-scrollbar-track { background: transparent; }
.scroll-row::-webkit-scrollbar-thumb { background: rgba(210,175,100,0.15); }

/* ── CITY CARD (active city brief) ── */
.city-card {
  border: 1px solid rgba(210,175,100,0.15);
  background: rgba(210,175,100,0.03);
  padding: 28px;
  position: relative;
  margin-bottom: 0;
}
.city-card::before, .city-card::after {
  content: ''; position: absolute; width: 14px; height: 14px;
}
.city-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.4); border-left: 1px solid rgba(210,175,100,0.4); }
.city-card::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.4); border-right: 1px solid rgba(210,175,100,0.4); }

.city-card-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  margin-bottom: 20px; gap: 16px;
}
.city-card-city {
  font-size: 9px; font-weight: 600; letter-spacing: 4px;
  color: rgba(210,175,100,0.5); text-transform: uppercase;
  margin-bottom: 6px;
}
.city-card-tagline {
  font-family: 'Cormorant', serif; font-size: 22px; font-weight: 400;
  color: #edeae2; line-height: 1.3; max-width: 420px;
}
.city-live {
  display: flex; align-items: center; gap: 7px; flex-shrink: 0;
  font-size: 8px; font-weight: 600; letter-spacing: 2px;
  color: rgba(210,175,100,0.5); text-transform: uppercase;
}
.city-live-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: rgba(210,175,100,0.7);
  box-shadow: 0 0 6px rgba(210,175,100,0.4);
  animation: pulse 2s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.city-divider { height: 1px; background: rgba(210,175,100,0.08); margin-bottom: 20px; }
.city-stats {
  display: flex; gap: 0; margin-bottom: 24px;
}
.city-stat {
  flex: 1; display: flex; flex-direction: column; gap: 4px;
}
.city-stat-value {
  font-family: 'Cormorant', serif; font-size: 28px; font-weight: 300;
  color: rgba(210,175,100,0.85); line-height: 1;
}
.city-stat-label {
  font-size: 7px; font-weight: 600; letter-spacing: 1.5px;
  color: rgba(237,232,223,0.2); text-transform: uppercase;
}
.city-cta {
  display: flex; align-items: center; gap: 12px;
  font-size: 8px; font-weight: 600; letter-spacing: 2.5px;
  color: rgba(210,175,100,0.7); text-transform: uppercase;
  cursor: pointer;
}
.city-cta-tick { width: 14px; height: 1px; background: rgba(210,175,100,0.3); flex-shrink: 0; }

.city-card-empty { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
.city-empty-title { font-family: 'Cormorant', serif; font-size: 22px; font-weight: 300; color: rgba(237,232,223,0.4); }
.city-empty-sub { font-size: 11px; font-weight: 300; color: rgba(237,232,223,0.25); margin-bottom: 18px; }

/* ── MEMBERS IN CITY STRIP ── */
.members-in-city {
  border: 1px solid rgba(210,175,100,0.1);
  background: rgba(210,175,100,0.02);
  padding: 20px 24px;
  margin-top: 8px;
  margin-bottom: 8px;
}
.mic-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 16px;
}
.mic-label {
  font-size: 8px; font-weight: 600; letter-spacing: 2px;
  color: rgba(210,175,100,0.35); text-transform: uppercase;
}
.mic-view-all {
  font-size: 8px; font-weight: 600; letter-spacing: 2px;
  color: rgba(210,175,100,0.35); text-transform: uppercase; cursor: pointer;
  transition: color 0.2s;
}
.mic-view-all:hover { color: rgba(210,175,100,0.6); }
.mic-avatars {
  display: flex; align-items: center; gap: 0;
}
.mic-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  border: 1px solid rgba(210,175,100,0.2);
  background: #0e0c09;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cormorant', serif; font-size: 14px; font-weight: 300;
  color: rgba(210,175,100,0.55);
  flex-shrink: 0;
}
.mic-count {
  font-size: 9px; font-weight: 400;
  color: rgba(237,232,223,0.2);
  margin-left: auto;
}

/* ── REQUESTED ARRANGEMENTS ── */
.wanted-list {
  border: 1px solid rgba(210,175,100,0.1);
  background: rgba(210,175,100,0.02);
}
.wanted-item {
  display: flex; align-items: center; gap: 18px;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(210,175,100,0.07);
}
.wanted-item:last-child { border-bottom: none; }
.wanted-rank {
  font-family: 'Cormorant', serif; font-size: 24px; font-weight: 300;
  color: rgba(210,175,100,0.4); width: 24px; flex-shrink: 0; text-align: center;
}
.wanted-body { flex: 1; min-width: 0; }
.wanted-name { font-family: 'Cormorant', serif; font-size: 20px; font-weight: 400; color: #edeae2; line-height: 1.2; }
.wanted-meta { font-size: 8px; font-weight: 600; letter-spacing: 1.8px; text-transform: uppercase; color: rgba(210,175,100,0.4); margin-top: 4px; }
.wanted-quote { font-size: 11.5px; font-weight: 300; font-style: italic; color: rgba(237,232,223,0.35); margin-top: 8px; line-height: 1.5; }
.wanted-count { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
.wanted-count-value { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 300; color: rgba(210,175,100,0.85); line-height: 1; }
.wanted-count-label { font-size: 7px; font-weight: 600; letter-spacing: 1.5px; color: rgba(237,232,223,0.2); text-transform: uppercase; }
.wanted-cta {
  display: flex; align-items: center; justify-content: center; gap: 14px;
  margin-top: 20px; padding: 16px 0;
  border: 1px solid rgba(210,175,100,0.18);
  font-size: 9px; font-weight: 600; letter-spacing: 3px;
  color: rgba(210,175,100,0.7); text-transform: uppercase;
  cursor: pointer; transition: border-color 0.2s, color 0.2s;
}
.wanted-cta:hover { border-color: rgba(210,175,100,0.5); color: rgba(210,175,100,1); }
.wanted-cta-tick { width: 16px; height: 1px; background: rgba(210,175,100,0.3); flex-shrink: 0; }

/* ── MY CONNECTIONS ── */
.connections-row {
  display: flex; gap: 32px;
  overflow-x: auto; padding-bottom: 12px;
  margin-bottom: 80px;
  scrollbar-width: thin;
  scrollbar-color: rgba(210,175,100,0.2) transparent;
}
.connections-row::-webkit-scrollbar { height: 1px; }
.connections-row::-webkit-scrollbar-track { background: transparent; }
.connections-row::-webkit-scrollbar-thumb { background: rgba(210,175,100,0.2); }
.conn-item {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  cursor: pointer; flex-shrink: 0; width: 80px; transition: opacity 0.2s;
}
.conn-item:hover { opacity: 0.8; }
.conn-avatar {
  width: 72px; height: 72px; border-radius: 50%;
  border: 1px solid rgba(210,175,100,0.2);
  overflow: hidden; background: rgba(210,175,100,0.05);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cormorant', serif; font-size: 22px; font-weight: 300;
  color: rgba(210,175,100,0.5); transition: border-color 0.2s;
}
.conn-item:hover .conn-avatar { border-color: rgba(210,175,100,0.5); }
.conn-avatar img { width: 100%; height: 100%; object-fit: cover; object-position: top; }
.conn-name {
  font-size: 11px; font-weight: 400;
  color: rgba(237,232,223,0.3); text-align: center; letter-spacing: 0.3px; line-height: 1.3;
}
.empty-connections { padding: 32px 0; margin-bottom: 80px; }

/* ── BOTTOM NAV ── */
.bottom-nav {
  position: fixed; bottom: 0; left: 0; right: 0; z-index: 20;
  background: #070708;
  border-top: 1px solid rgba(210,175,100,0.1);
  display: flex; align-items: center; justify-content: space-around;
  padding: 14px 0 18px;
}
.bottom-nav-glow {
  position: absolute; top: -1px; left: 10%; right: 10%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent);
  pointer-events: none;
}
.nav-item {
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  cursor: pointer; padding: 4px 20px;
  color: rgba(237,232,223,0.2);
  transition: color 0.2s;
  background: none; border: none;
  font-family: 'Montserrat', sans-serif;
  min-width: 60px;
}
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .cards-grid { grid-template-columns: repeat(2, 1fr); }
  .page { padding: 28px 28px 120px; }
  .section-title { font-size: 34px; }
  .mission-title { font-size: 40px; }
  .city-card-tagline { font-size: 18px; }
}
@media (max-width: 600px) {
  .cards-grid { grid-template-columns: 1fr; }
  .page { padding: 24px 20px 120px; }
  .topbar-name { font-size: 22px; }
  .frame, .frame-inner { inset: 12px; }
  .nav-item { padding: 4px 12px; }
  .city-stats { flex-direction: column; gap: 16px; }
  .wanted-item { gap: 12px; padding: 16px; }
  .wanted-name { font-size: 17px; }
  .mission-title { font-size: 32px; }
}
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}
</style>